import moment from 'moment-timezone';
import 'moment/locale/fr.js';
import { RecurringRule } from '../models/RecurringRules.js';
moment.locale('fr');

const defaultTimezone = "America/Toronto";

export class DateUtil {

    public static getFirstAndLastDayOfMonth = (monthIndex: number) => {

        const currentMonthIndex = new Date().getMonth();
        const currentYear = new Date().getFullYear();
        const year = monthIndex >= currentMonthIndex ? currentYear : currentYear + 1;

        // First day of the month
        const firstDate = new Date(year, monthIndex, 1);
        const firstDay = firstDate.toISOString().split('T')[0];

        // Last day of the month
        const lastDate = new Date(year, monthIndex + 1, 0); // Day 0 of next month is the last day of current month
        const lastDay = lastDate.toISOString().split('T')[0];

        return { firstDay, lastDay };
    };

    public static getFrenchMonthName = (monthNumber?: string): string => {
        if (!monthNumber) {
            return '';
        }
        const date = new Date();
        date.setMonth(parseInt(monthNumber) - 1);
        const monthName = date.toLocaleString('fr-FR', { month: 'long' });

        return monthName.charAt(0).toUpperCase() + monthName.slice(1);
    };

    public static getDayNameInFrench = (dateString: string) => {

        if (!dateString) {
            return '';
        }
        const date = new Date(dateString);

        // Get the day name in French
        return date.toLocaleDateString('fr-CA', { weekday: 'long', timeZone: defaultTimezone });
    };

    public static getDayNumber = (dateString: string) => {
        if (!dateString) {
            return '';
        }

        const day = new Date(dateString).toLocaleString("fr-CA", {
            timeZone: defaultTimezone,
            day: "numeric"
        });
        return day;
    };

    public static getMonthNumber = (dateString: string) => {
        if (!dateString) {
            return '';
        }

        const month = new Date(dateString).toLocaleString("fr-CA", {
            timeZone: defaultTimezone,
            month: "numeric"
        });
        return month;
    };

    public static getHour = (dateString: string): number => {
        if (!dateString) {
            return 12;
        }

        const hour = new Date(dateString).getHours();
        return hour;
    };

    public static isValidDateString = (str: string): boolean => {
        // Check format using regex
        const regex = /^\d{4}-\d{2}-\d{2}$/;
        if (!regex.test(str)) {
            return false;
        }

        // Try to create a real Date
        const date = new Date(str);
        if (Number.isNaN(date.getTime())) {
            return false;
        }

        // Extra: check if the parts match (avoid JS weird autocorrections)
        const [year, month, day] = str.split('-').map(Number);
        return (
            date.getUTCFullYear() === year &&
            date.getUTCMonth() + 1 === month &&
            date.getUTCDate() === day
        );
    };

    public static createLocalDateFromYMD = (ymdString: string): Date => {
        const [year, month, day] = ymdString.split('-').map(Number);
        return new Date(year, month - 1, day); // Month is 0-indexed!
    };


    public static formatDateToLocalYMD = (date: Date | null): string => {
        if (!date) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0'); // JS months 0-11
        const day = String(date.getDate()).padStart(2, '0');

        return `${year}-${month}-${day}`;
    }

    public static convertToDefaultTimeZoneLong = (dateString: string, timeUnkown: boolean = false) => {
        if (!dateString) {
            return '';
        }

        // Create a Date object from the input string
        const date = new Date(dateString);

        const options = {
            timeZone: defaultTimezone,
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: timeUnkown ? undefined : 'numeric',
            minute: timeUnkown ? undefined : '2-digit'
        };
        return date.toLocaleDateString('fr-CA', options as any);
    }

    public static convertToAmericaToronto = (dateString: string, timeUnkown: boolean = false) => {

        if (!dateString) {
            return '';
        }

        // Create a Date object from the input string
        const date = new Date(dateString);

        // Format as YYYY-MM-DD HH:MM:SS in America/Toronto
        const options = {
            timeZone: 'America/Toronto',
            year: 'numeric',
            month: '2-digit',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        };
        
        const estDate = date.toLocaleString('fr-CA', options as any);

        // Reformat the date
        const [year, month, day, hour, minute] = estDate.match(/\d+/g) || [];
        let time = timeUnkown ? '' : `${hour}h${parseInt(minute) > 0 ? minute : ''}`;
        return `${day}/${month}/${year} ${time}`;

    }

    public static formatTimeFromDateInDefaultTimezone = (dateString: string): string => {
        if (!dateString) {
            return '';
        }

        const date = new Date(dateString);
        const formatted = date.toLocaleString('fr-CA', {
            timeZone: defaultTimezone,
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        });
        const [, , hour, minute] = formatted.match(/\d+/g) || [];

        if (!hour || !minute) {
            return '';
        }

        return `${hour}h${parseInt(minute, 10) > 0 ? minute : ''}`;
    }

    public static formatDate = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0'); // months are 0-based
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    public static toDatetimeLocal = (date: Date) => {
        const pad = (n: number) => String(n).padStart(2, "0");
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
    }

    public static getWeekDays = (nextWeek?: boolean) => {
        const today = new Date();
        if (nextWeek) {
            today.setDate(today.getDate() + 7);
        }
        const dayOfWeek = today.getDay(); // 0 (Sun) - 6 (Sat)
        const distanceToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Adjust for Sunday
        const monday = new Date(today);
        monday.setDate(today.getDate() - distanceToMonday);

        // Calculate Sunday (7 days after Monday)
        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);

        // Format as YYYY-MM-DD
        const formatDate = (date: Date) => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        };

        return {
            monday: formatDate(monday),
            sunday: formatDate(sunday)
        };
    }

    public static getPeriodLabel = (
        start: string,
        end: string,
        tz = "America/Toronto",
        locale = "fr-CA"
    ) => {
        let s = moment.tz(start, tz);
        let e = moment.tz(end, tz);

        // Safety: if end < start, swap
        if (e.isBefore(s)) [s, e] = [e, s];

        const fullDateFormatter = new Intl.DateTimeFormat(locale, {
            timeZone: tz,
            day: "numeric",
            month: "long",
            year: "numeric",
        });

        const getDateParts = (value: Date) => {
            const parts = fullDateFormatter.formatToParts(value);
            return {
                day: parts.find((part) => part.type === "day")?.value ?? "",
                month: parts.find((part) => part.type === "month")?.value ?? "",
                year: parts.find((part) => part.type === "year")?.value ?? "",
            };
        };

        const startParts = getDateParts(s.toDate());
        const endParts = getDateParts(e.toDate());

        const sameYear = s.year() === e.year();
        const sameMonth = sameYear && s.month() === e.month();

        if (sameYear) {
            // e.g., "25–31 août 2025" or "28 août – 3 septembre 2025"
            return sameMonth
                ? `${startParts.day}–${endParts.day} ${endParts.month} ${endParts.year}`
                : `${startParts.day} ${startParts.month} – ${endParts.day} ${endParts.month} ${endParts.year}`;
        }

        // Cross-year: show the year on BOTH sides
        // e.g., "25 décembre 2025 – 5 janvier 2026"
        return `${startParts.day} ${startParts.month} ${startParts.year} – ${endParts.day} ${endParts.month} ${endParts.year}`;
    };

    public static DAYS_FR = [
        'dimanche',
        'lundi',
        'mardi',
        'mercredi',
        'jeudi',
        'vendredi',
        'samedi'
    ];

    public static formatTimeFR = (time: string): string => {
        const [h, m] = time.split(':')
        return m === '00' ? `${Number(h)} h` : `${Number(h)} h ${m}`
    }

    public static recurringRuleToText = (rule: RecurringRule, showTime: boolean): string => {
        const hasTime = Boolean(rule.time)
        const timeSuffix = showTime && hasTime ? ` à ${this.formatTimeFR(rule.time)}` : ''
        const startSuffix = rule.startDate
            ? ` à partir du ${this.convertToDefaultTimeZoneLong(rule.startDate, true)}`
            : ''
        const untilSuffix = rule.until
            ? ` jusqu'au ${this.convertToDefaultTimeZoneLong(rule.until, true)}`
            : ''

        switch (rule.frequency) {
            case 'weekly': {
                if (rule.dayOfWeek == null) return ''

                const day = this.DAYS_FR[rule.dayOfWeek]

                if (rule.interval === 1) {
                    return `Tous les ${day}s${timeSuffix}${startSuffix}${untilSuffix}`
                }

                return `Toutes les ${rule.interval} semaines, le ${day}${timeSuffix}${startSuffix}${untilSuffix}`
            }

            case 'daily': {
                if (rule.interval === 1) {
                    return `Tous les jours${timeSuffix}${startSuffix}${untilSuffix}`
                }

                return `Tous les ${rule.interval} jours${timeSuffix}${startSuffix}${untilSuffix}`
            }

            case 'monthly': {
                if (rule.monthDay != null) {
                    return `Le ${rule.monthDay} de chaque mois${timeSuffix}${startSuffix}${untilSuffix}`
                }

                return `Chaque mois${timeSuffix}${startSuffix}${untilSuffix}`
            }

            case 'yearly': {
                if (rule.month != null && rule.monthDay != null) {
                    return `Chaque année le ${rule.monthDay} ${this.getFrenchMonthName(String(rule.month + 1))}${timeSuffix}${startSuffix}${untilSuffix}`
                }

                return `Chaque année${timeSuffix}${startSuffix}${untilSuffix}`
            }

            default:
                return ''
        }
    }

}
